import {
  ArrowRight,
  Check,
  FileCheck2,
  FileSpreadsheet,
  LoaderCircle,
  RefreshCw,
  UploadCloud,
  X,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useAuth } from '../auth/useAuth';
import { requireFirebaseFunctions } from '../lib/firebase';
import {
  formatFileSize,
  VALIDATION_REPORT_TYPES,
  validateValidationWorkbook,
  validationReportTypeLabel,
  type ValidationReportType,
} from '../lib/validation';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const now = new Date();
const currentYear = now.getFullYear();
const years = Array.from({ length: currentYear - 2024 + 3 }, (_, index) => 2024 + index);

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

type UploadValidationWorkbookResponse = {
  validationId: string;
  reference: {
    id: string;
    name: string;
    webViewLink?: string;
  };
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Impossibile leggere il file selezionato.'));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Impossibile leggere il file selezionato.'));
        return;
      }

      resolve(reader.result.slice(reader.result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function ValidationPage() {
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [reportType, setReportType] = useState<ValidationReportType>('complete');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedReference, setUploadedReference] = useState<UploadValidationWorkbookResponse | null>(null);
  const [dragging, setDragging] = useState(false);

  const periodLabel = `${MONTHS[month - 1]} ${year}`;
  const typeLabel = validationReportTypeLabel(reportType);
  const ready = Boolean(file) && !fileError && status !== 'uploading';
  const selectionSummary = useMemo(() => ({ periodLabel, typeLabel }), [periodLabel, typeLabel]);

  const chooseFile = (candidate: File | undefined) => {
    if (!candidate) return;
    const error = validateValidationWorkbook(candidate);
    setFileError(error);
    setFile(error ? null : candidate);
    setStatus('idle');
    setUploadError(null);
    setUploadedReference(null);
  };

  const removeFile = () => {
    setFile(null);
    setFileError(null);
    setStatus('idle');
    setUploadError(null);
    setUploadedReference(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    chooseFile(event.target.files?.[0]);
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    chooseFile(event.dataTransfer.files?.[0]);
  };

  const uploadReference = async () => {
    if (!file || fileError) return;
    setStatus('uploading');
    setUploadError(null);

    try {
      if (user?.demo) {
        setUploadedReference({
          validationId: 'demo',
          reference: { id: 'demo', name: file.name },
        });
      } else {
        const uploadValidationWorkbook = httpsCallable<{
          fileName: string;
          contentsBase64: string;
          year: number;
          month: number;
          reportType: ValidationReportType;
        }, UploadValidationWorkbookResponse>(requireFirebaseFunctions(), 'uploadValidationWorkbook');

        const result = await uploadValidationWorkbook({
          fileName: file.name,
          contentsBase64: await fileToBase64(file),
          year,
          month,
          reportType,
        });
        setUploadedReference(result.data);
      }

      setStatus('success');
    } catch {
      setStatus('error');
      setUploadError('Non è stato possibile caricare l’Excel su Drive. Riprova tra qualche istante.');
    }
  };

  const reset = () => {
    removeFile();
  };

  return (
    <main className="dashboard-page validation-page">
      <header className="page-heading validation-heading">
        <div>
          <p className="app-eyebrow">Supervisione</p>
          <h1>Verifica</h1>
          <p>Confronta un report generato con l’Excel manuale dello stesso periodo.</p>
        </div>
      </header>

      <div className="validation-grid">
        <section className="validation-form-card" aria-labelledby="validation-selection-title">
          <header className="validation-card-heading">
            <span className="validation-card-icon"><FileCheck2 size={22} /></span>
            <div>
              <p>Confronto</p>
              <h2 id="validation-selection-title">Seleziona il report generato</h2>
              <span>Indica periodo e tipologia del report da verificare.</span>
            </div>
          </header>

          <div className="validation-fields">
            <label>
              <span>Anno</span>
              <select value={year} disabled={status === 'uploading'} onChange={(event) => setYear(Number(event.target.value))}>
                {years.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label>
              <span>Mese</span>
              <select value={month} disabled={status === 'uploading'} onChange={(event) => setMonth(Number(event.target.value))}>
                {MONTHS.map((item, index) => <option key={item} value={index + 1}>{item}</option>)}
              </select>
            </label>
            <label>
              <span>Tipo di report</span>
              <select value={reportType} disabled={status === 'uploading'} onChange={(event) => setReportType(event.target.value as ValidationReportType)}>
                {VALIDATION_REPORT_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
          </div>

          <div className="validation-section-divider" />

          <div className="validation-upload-heading">
            <div>
              <p>Excel di riferimento</p>
              <h2>Carica il report manuale</h2>
            </div>
            <span>.xlsx o .xls · max 8 MB</span>
          </div>

          {!file ? (
            <label
              className={`validation-dropzone${dragging ? ' dragging' : ''}${fileError ? ' invalid' : ''}`}
              onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              <input ref={inputRef} type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={handleFileInput} />
              <span><UploadCloud size={25} /></span>
              <strong>Trascina qui il file Excel</strong>
              <p>oppure selezionalo dal computer</p>
              <em>Scegli file</em>
            </label>
          ) : (
            <div className="validation-file-row">
              <span className="validation-file-icon"><FileSpreadsheet size={23} /></span>
              <span className="validation-file-copy">
                <strong>{file.name}</strong>
                <small>Excel · {formatFileSize(file.size)}</small>
              </span>
              <span className="validation-file-ready"><Check size={14} /> Pronto</span>
              <button type="button" aria-label="Rimuovi file" disabled={status === 'uploading'} onClick={removeFile}><X size={17} /></button>
            </div>
          )}

          {fileError && <div className="validation-feedback error" role="alert">{fileError}</div>}
          {uploadError && <div className="validation-feedback error" role="alert">{uploadError}</div>}
          {status === 'success' && uploadedReference && (
            <div className="validation-feedback success" role="status">
              <Check size={16} />
              <span>
                <strong>Excel caricato e confronto registrato.</strong>
                {uploadedReference.reference.webViewLink ? (
                  <a href={uploadedReference.reference.webViewLink} target="_blank" rel="noreferrer">Apri il file su Drive <ArrowRight size={13} /></a>
                ) : ' Il riferimento è pronto.'}
              </span>
            </div>
          )}
        </section>

        <aside className="validation-summary-card" aria-label="Riepilogo confronto">
          <p>Riepilogo</p>
          <h2>Confronto da preparare</h2>
          <dl>
            <div><dt>Periodo</dt><dd>{selectionSummary.periodLabel}</dd></div>
            <div><dt>Report generato</dt><dd>{selectionSummary.typeLabel}</dd></div>
            <div><dt>Excel manuale</dt><dd>{file?.name ?? 'Da caricare'}</dd></div>
          </dl>
          <div className={`validation-readiness${ready || status === 'success' ? ' ready' : ''}`}>
            {ready || status === 'success' ? <Check size={16} /> : <RefreshCw size={16} />}
            <span>
              <strong>{status === 'success' ? 'Riferimento acquisito' : ready ? 'Dati completi' : 'In attesa dell’Excel'}</strong>
              {status === 'success' ? 'La verifica è stata registrata.' : ready ? 'Puoi preparare il confronto.' : 'Seleziona un file per continuare.'}
            </span>
          </div>
          {status === 'success' ? (
            <button className="button button-secondary" type="button" onClick={reset}><RefreshCw size={16} /> Nuovo confronto</button>
          ) : (
            <button className="button button-primary" type="button" disabled={!ready} onClick={() => void uploadReference()}>
              {status === 'uploading' ? <LoaderCircle className="spin" size={17} /> : <UploadCloud size={17} />}
              {status === 'uploading' ? 'Caricamento…' : 'Carica e prepara'}
            </button>
          )}
          <small>Il confronto dei valori sarà eseguito sul report generato selezionato.</small>
        </aside>
      </div>
    </main>
  );
}
